import type { ImportFile } from '@/app/ai/hooks/useImportFilesToGrid';
import type { ContextSegmentSizes } from '@/app/ai/utils/contextMeasurement';
import type { AITool, AIToolsArgs } from 'quadratic-shared/ai/specs/aiToolsSpec';
import type { AIModelKey, AISource, AIUsage, Chat, Content, Context } from 'quadratic-shared/typesAndSchemasAI';
import type { CategorizedEmptyChatPromptSuggestions } from '@/app/ai/hooks/useGetEmptyChatPromptSuggestions';

/**
 * Type for prompt suggestions extracted from AI tool calls
 */
export type PromptSuggestions = AIToolsArgs[AITool.UserPromptSuggestions]['prompt_suggestions'];

/**
 * State for tracking context usage from AI API responses
 */
export interface ContextUsageState {
  /** The latest usage data from the most recent API response */
  usage: AIUsage | undefined;
}

/**
 * One entry in the per-chat history of provider-reported usage data.
 *
 * Captures the AIUsage returned for a single API round so the UI and
 * regression tests can chart growth across a conversation.
 */
export interface ContextUsageHistoryEntry {
  usage: AIUsage;
  timestamp: number;
  source: AISource;
  modelKey: AIModelKey | undefined;
}

/**
 * Bounded history of usage entries. {@link CONTEXT_USAGE_HISTORY_LIMIT} caps
 * the size to prevent unbounded growth in long-running sessions.
 */
export type ContextUsageHistoryState = ContextUsageHistoryEntry[];

export const CONTEXT_USAGE_HISTORY_LIMIT = 100;

/**
 * Snapshot of the per-segment character/token breakdown from the most recent
 * call to ContextBuilder.buildContext. Drives the per-segment indicator UI and
 * developer tooling.
 */
export type ContextSegmentSizesState = ContextSegmentSizes | undefined;

/**
 * State for empty chat suggestions (categorized prompts shown when chat is empty)
 */
export interface EmptyChatSuggestionsState {
  suggestions: CategorizedEmptyChatPromptSuggestions | undefined;
  contextHash: string | undefined;
  loading: boolean;
  abortController: AbortController | undefined;
}

/**
 * State for prompt suggestions with abort controller
 */
export interface PromptSuggestionsState {
  abortController: AbortController | undefined;
  suggestions: PromptSuggestions;
}

/**
 * State for PDF import operations
 */
export interface PdfImportState {
  abortController: AbortController | undefined;
  loading: boolean;
}

/**
 * State for web search operations
 */
export interface WebSearchState {
  abortController: AbortController | undefined;
  loading: boolean;
}

/**
 * State for import files to grid operations
 */
export interface ImportFilesToGridState {
  loading: boolean;
}

/**
 * State for tracking failing SQL connections
 */
export interface FailingSqlConnectionsState {
  uuids: string[];
  lastResetTimestamp: number;
}

/**
 * A prompt waiting in the queue to be executed after the current one finishes
 */
export interface QueuedPrompt {
  id: string;
  content: Content;
  context: Context;
  importFiles: ImportFile[];
}

/**
 * Creates a new empty chat with a fresh timestamp
 */
export const createDefaultChat = (): Chat => ({
  id: '',
  name: '',
  lastUpdated: Date.now(),
  messages: [],
});
